import { cloneDeep } from 'lodash'
import type {
  TEntityData,
  TEntityId,
  TPartialEntityData,
  IEntityParentObjectForCreate,
  TEntityVersion,
} from '../types/models'
import { assert, assertIsNotUndefined } from '../types/models'
import {
  writeBackHridsAndVersions,
  writeBackVersions,
} from './entityWriteBack'
import {
  assertIsEntityActionsResponse,
  isEntityActionsPayload,
  toEntityActionsWireBody,
} from './entityActions'
import type { IEntityActionsPayload } from './entityActions'
import type { ISubentityAfterRequestCallback } from './setShowEntityDialog'
import { getNextEntityId } from './getNextEntityId'
import {
  createEntity,
  updateEntity,
  deleteEntity,
  writeEntityActions,
} from '~/services/crud'
import { UpdateConflictError } from '~/services/errors'
import { useEntityDialogStore } from '~/stores/entityDialog'
import { useLoggedInUserStore } from '~/stores/loggedInUser'

const mn = '[cynqueModules/saveEntityData.ts] |'

/**
 * Thrown when the write itself SUCCEEDED but `afterRequestCallback` blew up.
 *
 * These two failures reach the caller's `catch` through the same channel but mean
 * opposite things to a user. The callback runs after the server has already
 * accepted and persisted the change, so a throw from it means "saved, but the
 * screen didn't refresh" — never "your edit was lost". Callers told the user
 * "Couldn't update …" for both, which sends people off to re-enter work that is
 * safely on the server. Distinguish it here so they can say something true.
 *
 * Still a real bug worth reporting — this type changes the USER-facing message,
 * not whether it reaches Honeybadger.
 */
export class AfterRequestCallbackError extends Error {
  constructor(cause: unknown) {
    super(
      `The save succeeded, but the after-request callback failed: ${cause}`,
      { cause }
    )
    this.name = 'AfterRequestCallbackError'
  }
}

/**
 * Invoke an after-request callback, tagging anything it throws.
 *
 * Deliberately does NOT swallow: a broken callback leaves the screen stale and
 * has to stay visible in Honeybadger.
 */
function runAfterRequestCallback(afterRequestCallback: Function, arg: object) {
  try {
    afterRequestCallback(arg)
  } catch (e) {
    console.error(`${mn} afterRequestCallback threw (the save succeeded) -`, e)
    throw new AfterRequestCallbackError(e)
  }
}

export function saveEntityData({
  entityName,
  entityData = undefined,
  entityId = undefined,
  newOrUpdatedEntityData,
  entityParentObjectForCreate,
  preRequestProcessor,
  afterRequestCallback,
  storeEditedInputDataAsJsonInProperty,
  isSubentityDialog,
  subentityAfterRequestProcessor,
  subentityAfterRequestCallbacks,
  subentityParentObjectForCreate,
}: {
  entityName: string
  entityData?: TEntityData
  entityId?: TEntityId
  newOrUpdatedEntityData: any
  entityParentObjectForCreate?: IEntityParentObjectForCreate
  preRequestProcessor?: Function
  afterRequestCallback: Function
  storeEditedInputDataAsJsonInProperty?: string | undefined
  isSubentityDialog: boolean
  subentityAfterRequestProcessor?: Function
  subentityAfterRequestCallbacks?: ISubentityAfterRequestCallback[] | undefined
  subentityParentObjectForCreate?: Function
}) {
  const fn = `${mn} saveEntityData |`
  if (entityData && !isSubentityDialog) {
    console.debug(
      `${fn} this.entityData is NOT null. Calling this.patchEntityData.`
    )
    if (!entityId) {
      throw new Error(`${fn} entityId is falsey`)
    }
    patchEntityData({
      entityName,
      entityId,
      entityData,
      updatedEntityData: newOrUpdatedEntityData,
      preRequestProcessor,
      afterRequestCallback,
      storeEditedInputDataAsJsonInProperty,
    })
  } else {
    console.debug(
      `${fn} this.entityData IS null (no pre-existing instance).` +
        `Calling this.postEntityData.`
    )
    postEntityData({
      entityName,
      newEntityData: newOrUpdatedEntityData,
      entityParentObjectForCreate,
      preRequestProcessor,
      afterRequestCallback,
      isSubentityDialog,
      subentityAfterRequestProcessor,
      subentityAfterRequestCallbacks,
      subentityParentObjectForCreate,
    })
  }
}

export async function postEntityData({
  entityName,
  newEntityData,
  entityParentObjectForCreate,
  preRequestProcessor,
  afterRequestCallback,
  isSubentityDialog = false,
  subentityAfterRequestProcessor,
  subentityAfterRequestCallbacks,
  subentityParentObjectForCreate,
  addUser = false,
}: {
  entityName: string
  newEntityData: any // can't type more strongly. Doesn't have predictable properties yet.
  entityParentObjectForCreate?: IEntityParentObjectForCreate
  preRequestProcessor?: Function
  afterRequestCallback?: Function
  isSubentityDialog?: boolean
  subentityAfterRequestProcessor?: Function
  subentityAfterRequestCallbacks?: ISubentityAfterRequestCallback[] | undefined
  subentityParentObjectForCreate?:
    | Function
    | IEntityParentObjectForCreate
    | undefined
  addUser?: boolean
}) {
  const fn = `${mn} postEntityData |`
  if (!newEntityData) {
    throw new Error(`newEntityData is falsey`)
  }
  if (!entityName) {
    throw new Error(`entityName is falsey`)
  }

  newEntityData = _addCreatedAtModifiedAt({
    fn,
    newEntityData,
  })

  newEntityData = _addCanBeDeleted({
    fn,
    newEntityData,
  })

  if (isSubentityDialog) {
    console.debug(`${fn} This IS a subentity Dialog (add parent?)`)
    if (subentityParentObjectForCreate) {
      console.debug(`${fn} There IS a subentityParentObjectForCreate`)
      if (typeof subentityParentObjectForCreate === 'function') {
        console.debug(`${fn} It's a function`)
        if (entityParentObjectForCreate) {
          console.debug(
            `${fn} ...and there is a passed entityParentObjectForCreate:`,
            entityParentObjectForCreate
          )
        } else {
          console.warn(
            `${fn} ...but there is NO passed entityParentObjectForCreate`
          )
        }
        const parent = await subentityParentObjectForCreate({
          entityParentObjectForCreate,
        })
        console.debug(`${fn} parent:`, parent)
        newEntityData = _addParent({
          fn,
          entityParentObjectForCreate: parent,
          newEntityData,
        })
      } else {
        console.debug(`${fn} It's an object (not a function)`)
        console.debug(`${fn} parent:`, subentityParentObjectForCreate)
        newEntityData = _addParent({
          fn,
          entityParentObjectForCreate: subentityParentObjectForCreate,
          newEntityData,
        })
      }
    } else {
      console.debug(`${fn} There is -NO- subentityParentObjectForCreate`)
    }
  } else if (entityParentObjectForCreate) {
    console.debug(`${fn} There IS an entityParentObjectForCreate`)
    newEntityData = _addParent({
      fn,
      entityParentObjectForCreate,
      newEntityData,
    })
  }

  newEntityData = _addId({
    fn,
    newEntityData,
    entityName,
  })

  if (addUser) {
    newEntityData = _addUser({
      fn,
      newEntityData,
    })
  }

  if (!isSubentityDialog) {
    if (preRequestProcessor) {
      console.debug(`${fn} There IS a preRequestProcessor`)
      newEntityData = await preRequestProcessor(newEntityData)
    } else {
      console.debug(`${fn} There is NO preRequestProcessor`)
    }
  }

  if (isEntityActionsPayload(newEntityData)) {
    return await _postEntityActions({
      fn,
      payload: newEntityData,
      isSubentityDialog,
      afterRequestCallback,
    })
  }

  try {
    console.debug(
      `${fn} Calling createEntity with entityName '${entityName}', newEntityData:`,
      cloneDeep(newEntityData)
    )
    const data = await createEntity({
      entityName,
      entity: newEntityData,
    })

    assertIsNotUndefined(data)

    console.debug(
      `${fn} createEntity with entityName '${entityName}' returned`,
      data
    )

    // The write-back lives in entityWriteBack.ts so the /actions flow can reuse
    // it rather than growing a third copy. No `expectedIds` here: this caller
    // has never checked the response order and turning that on would be a
    // behaviour change, not a refactor.
    writeBackHridsAndVersions({ fn, newEntityData, data })

    if (isSubentityDialog) {
      console.debug(
        `${fn} This IS a subentity Dialog (subentityAfterRequestProcessor?)`
      )
      if (subentityAfterRequestProcessor) {
        newEntityData = subentityAfterRequestProcessor(newEntityData)
        console.debug(
          `${fn} After subentityAfterRequestProcessor processing...`,
          newEntityData
        )
      }
      if (subentityAfterRequestCallbacks) {
        console.debug(
          `${fn} There are ${subentityAfterRequestCallbacks.length}` +
            ` subEntiyAfterRequestCallbacks.Calling...`
        )
        subentityAfterRequestCallbacks.forEach((callback) => {
          console.debug(`${fn} callback entity: ${callback.entityName}`)
          if (entityName === callback.entityName) {
            console.debug(`${fn} calling callback for ${callback.entityName}`)
            callback.callback({ newEntityData })
          } else {
            console.debug(
              `${fn} NOT calling callback for ${callback.entityName}.` +
                ` Doesn't match entity '${entityName}.`
            )
          }
        })
      } else {
        console.debug(`${fn} There are NO subentityAfterRequestCallbacks`)
      }

      useEntityDialogStore().setSubentityReturnValue(newEntityData)
      return
    }
    console.debug(`${fn} This IS NOT a subentity Dialog`)

    if (!afterRequestCallback) {
      throw new Error(`${fn} There is NO afterRequestCallback`)
    }
    console.debug(`${fn} There IS an afterRequestCallback. Calling it.`)
    runAfterRequestCallback(afterRequestCallback, { newEntityData })
  } catch (e) {
    console.error(`${fn} error -`, e)
    throw e
  }
}

export async function patchEntityData({
  entityName,
  entityId,
  entityData,
  updatedEntityData,
  preRequestProcessor,
  afterRequestCallback,
  storeEditedInputDataAsJsonInProperty,
  onConflictRefetch,
}: {
  entityName: string
  entityId: TEntityId | undefined // Will be undefined if we're patching multiple entity instances
  entityData?: TEntityData
  updatedEntityData: any // can't type more strongly. Doesn't have definitive properties.
  preRequestProcessor?: Function
  afterRequestCallback: Function
  storeEditedInputDataAsJsonInProperty?: string | undefined
  // Opt-in conflict recovery. When the API rejects this PATCH with a 409 because
  // the submitted `version` is not the stored one, this is called to refetch the
  // row; the PATCH is then retried ONCE with the version it returns. Omit it and
  // a 409 propagates unchanged.
  onConflictRefetch?: () => Promise<{ version?: TEntityVersion } | undefined>
}) {
  const fn = `${mn} patchEntityData |`
  if (!updatedEntityData) {
    throw new Error(`${fn} updatedEntityData is falsey`)
  }
  if (!entityName) {
    throw new Error(`${fn} entityName is falsey`)
  }
  if (!Array.isArray(updatedEntityData)) {
    if (!entityId) {
      throw new Error(
        `${fn} updatedEntityData is -not- an array, and entityId is falsey`
      )
    }
  } else if (entityId) {
    throw new Error(
      `${fn} updatedEntityData is an array, and entityId is not falsey`
    )
  }

  if (!storeEditedInputDataAsJsonInProperty) {
    updatedEntityData = _addOrUpdateModifiedAt({
      fn,
      updatedEntityData,
    })
  }
  if (preRequestProcessor) {
    console.debug(`${fn} There IS a preRequestProcessor`)
    updatedEntityData = await preRequestProcessor(updatedEntityData)
  } else {
    console.debug(`${fn} There is NO preRequestProcessor`)
  }

  // ⚠ THE UPDATE-PATH MIRROR of the check in `postEntityData`. An edit dialog
  // whose `preRequestProcessor` returns an actions payload is saying "this one
  // save is really several writes, and they have to land together" — signing a
  // contract, which must also create the order. Without this the payload would
  // fall through to `updateEntity` and be PATCHed as if `{ __kind, actions }`
  // were entity fields.
  //
  // `entityId` is deliberately not forwarded: an actions payload addresses its
  // own rows by id, and the dialog's entity is just one of them.
  if (isEntityActionsPayload(updatedEntityData)) {
    return await _postEntityActions({
      fn,
      payload: updatedEntityData,
      isSubentityDialog: false,
      afterRequestCallback,
    })
  }

  try {
    if (storeEditedInputDataAsJsonInProperty) {
      console.debug(`${fn} FieldSetData to update:`, entityData)
      console.debug(`${fn} FieldSetData merge in:`, updatedEntityData)
      updatedEntityData = {
        [storeEditedInputDataAsJsonInProperty]: JSON.stringify({
          ...entityData,
          ...updatedEntityData,
        }),
      }
      console.debug(
        `${fn} FieldSetData wrapped and stringified for storage:`,
        updatedEntityData
      )
    }

    console.debug(
      `${fn} Calling updateEntity with entityName '${entityName}', updatedEntityData:`,
      updatedEntityData,
      `entityId '${entityId}'`
    )
    const data = await _updateEntityRetryingOneConflict({
      fn,
      entityName,
      updatedEntityData,
      onConflictRefetch,
      runUpdate: () =>
        updateEntity({
          entityName,
          entityId,
          updatedEntityData,
        }),
    })

    assertIsNotUndefined(data)

    console.debug(
      `${fn} updateEntity with entityName '${entityName}' returned`,
      data
    )

    // See the note in entityWriteBack.ts. Same reasoning as the create side:
    // no `expectedIds`, because this caller never checked.
    writeBackVersions({ fn, updatedEntityData, data })

    if (!afterRequestCallback) {
      throw new Error(`${fn} There is NO afterRequestCallback`)
    }
    console.debug(`${fn} There IS an afterRequestCallback`)
    runAfterRequestCallback(afterRequestCallback, {
      updatedEntityData,
      entityId,
    })
  } catch (e) {
    console.error(`${fn} error -`, e)
    throw e
  }
}

export async function deleteEntityData({
  entityName,
  entityDataToDelete,
  afterRequestCallback,
}: {
  entityName: string
  entityDataToDelete: TEntityData
  afterRequestCallback: Function
}) {
  const fn = `${mn} deleteEntityData |`
  if (!entityDataToDelete) {
    throw new Error(`${fn} entityDataToDelete is falsey`)
  }
  if (!entityName) {
    throw new Error(`${fn} entityName is falsey`)
  }
  if (!Array.isArray(entityDataToDelete) && !entityDataToDelete.id) {
    throw new Error(`${fn} entityId is falsey`)
  }

  try {
    const data = await deleteEntity({
      entityName,
      entityData: entityDataToDelete,
    })
    console.debug(
      `${fn} deleteEntity with entityName '${entityName}' returned`,
      data
    )
    if (!afterRequestCallback) {
      throw new Error(`${fn} There is NO afterRequestCallback`)
    }
    console.debug(`${fn} There IS an afterRequestCallback`)
    runAfterRequestCallback(afterRequestCallback, {
      deletedEntityData: entityDataToDelete,
    })
  } catch (e) {
    console.error(`${fn} error -`, e)
    throw e
  }
}

//
// Helper functions
//

function _addId({
  fn,
  newEntityData,
  entityName,
}: {
  fn: string
  newEntityData: TEntityData
  entityName: string
}) {
  console.debug(`${fn} newEntityData, before adding id...`)
  console.dir(cloneDeep(newEntityData))

  const newId = getNextEntityId({
    entityName,
  })

  const newEntityDataWithId = {
    id: newId,
    ...newEntityData,
  }

  console.debug(`${fn} newEntityData with id...`)
  console.dir(cloneDeep(newEntityDataWithId))
  return newEntityDataWithId
}

function _addParent({
  fn,
  entityParentObjectForCreate,
  newEntityData,
}: {
  fn: string
  entityParentObjectForCreate: IEntityParentObjectForCreate
  newEntityData: TEntityData
}) {
  console.debug(`${fn} entityParentObjectForCreate...`)
  console.dir(entityParentObjectForCreate)
  console.debug(`${fn} newEntityData, pre-processing...`)
  console.dir(cloneDeep(newEntityData))

  const newEntityDataProcessed = {
    [entityParentObjectForCreate.entityName]:
      entityParentObjectForCreate.object,
    ...newEntityData,
  }
  console.debug(`${fn} newEntityDataProcessed...`)
  console.dir(cloneDeep(newEntityDataProcessed))
  return newEntityDataProcessed
}

function _addUser({
  fn,
  newEntityData,
}: {
  fn: string
  newEntityData: TEntityData
}) {
  console.debug(`${fn} newEntityData, before adding user...`)
  console.dir(cloneDeep(newEntityData))

  const loggedInUser = useLoggedInUserStore().getLoggedInUser
  assertIsNotUndefined(loggedInUser) // should never be

  const newEntityDataWithUser = {
    user: loggedInUser,
    ...newEntityData,
  }

  console.debug(`${fn} newEntityData with user...`)
  console.dir(cloneDeep(newEntityDataWithUser))
  return newEntityDataWithUser
}

function _addCanBeDeleted({
  fn,
  newEntityData,
}: {
  fn: string
  newEntityData: TEntityData
}) {
  console.debug(`${fn} newEntityData, before adding canBeDeleted...`)
  console.dir(newEntityData)

  const newEntityDataWithCanBeDeleted = {
    ...newEntityData,
    canBeDeleted: true,
  }
  console.debug(`${fn} newEntityData with canBeDeleted...`)
  console.dir(newEntityDataWithCanBeDeleted)
  return newEntityDataWithCanBeDeleted
}

function _addCreatedAtModifiedAt({
  fn,
  newEntityData,
}: {
  fn: string
  newEntityData: TEntityData
}) {
  console.debug(
    `${fn} newEntityData, before adding createdAt and modifiedAt...`
  )
  console.dir(cloneDeep(newEntityData))

  const d = new Date()
  const datetime = d.toISOString()

  const newEntityDataWithCreatedAtAndModifiedAt = {
    ...newEntityData,
    createdAt: datetime,
    modifiedAt: datetime,
  }
  console.debug(`${fn} newEntityDataWithCreatedAtAndModifiedAt...`)
  console.dir(cloneDeep(newEntityDataWithCreatedAtAndModifiedAt))
  return newEntityDataWithCreatedAtAndModifiedAt
}

/**
 * `updateEntity`, retried ONCE against a version conflict.
 *
 * The version travelling in a PATCH is read from an in-memory copy of the row,
 * and that copy goes stale as soon as anything else writes the same row — a
 * second component holding its own copy, or a save whose response has not landed
 * yet. The API answers a stale version with a 409, and because the response never
 * reaches `afterRequestCallback` the local version is never refreshed, so every
 * later save repeats the same stale version: the conflict is self-perpetuating
 * rather than a one-off race. Refetching and retrying once breaks that loop.
 *
 * Deliberately ONE retry: a second conflict means something is genuinely writing
 * concurrently, and a retry loop against a live competitor is how you get a
 * hot loop rather than a resolution.
 *
 * Only for single-entity patches. A batch (array) patch would need per-item
 * versions reconciled against the refetched set, which no caller needs yet, so
 * the conflict propagates untouched.
 */
async function _updateEntityRetryingOneConflict({
  fn,
  entityName,
  updatedEntityData,
  onConflictRefetch,
  // The write itself, as a thunk. Passing the call rather than its arguments
  // keeps `updateEntity` invoked from exactly ONE place, so this helper owns the
  // retry policy and nothing else.
  runUpdate,
}: {
  fn: string
  entityName: string
  updatedEntityData: any
  onConflictRefetch?: () => Promise<{ version?: TEntityVersion } | undefined>
  runUpdate: () => Promise<any>
}) {
  try {
    return await runUpdate()
  } catch (e) {
    if (
      !(e instanceof UpdateConflictError) ||
      !onConflictRefetch ||
      Array.isArray(updatedEntityData)
    ) {
      throw e
    }
    console.warn(
      `${fn} 409 conflict patching '${entityName}' — the submitted version` +
        ` '${updatedEntityData.version}' is stale. Refetching and retrying once.`
    )
    console.debug(`${fn} conflict body:`, e.body)

    // A refetch that itself fails must not replace the conflict: the conflict is
    // the real story, and swapping in a NetworkError from the recovery attempt
    // would send a misleading error onward.
    let refetched
    try {
      refetched = await onConflictRefetch()
    } catch (refetchError) {
      console.error(`${fn} conflict refetch failed -`, refetchError)
      throw e
    }
    if (!refetched?.version) {
      console.error(`${fn} conflict refetch returned no version. Giving up.`)
      throw e
    }
    if (refetched.version === updatedEntityData.version) {
      // The stored version matches what we already sent, so the 409 was not
      // about staleness at all (a unique constraint, say). Retrying would just
      // repeat it.
      console.error(
        `${fn} refetched version '${refetched.version}' is the one already` +
          ` submitted — this 409 is not a stale-version conflict. Giving up.`
      )
      throw e
    }
    console.debug(
      `${fn} retrying with refetched version '${refetched.version}'.`
    )
    updatedEntityData.version = refetched.version
    return await runUpdate()
  }
}

function _addOrUpdateModifiedAt({
  fn,
  updatedEntityData,
}: {
  fn: string
  updatedEntityData: TPartialEntityData
}): TPartialEntityData {
  console.debug(`${fn} updatedEntityData, before adding modifiedAt...`)
  console.dir(cloneDeep(updatedEntityData))

  const datetime = new Date().toISOString()

  const updatedEntityDataWithModifiedAt = Array.isArray(updatedEntityData)
    ? updatedEntityData.map((ued) => ({ ...ued, modifiedAt: datetime }))
    : { ...updatedEntityData, modifiedAt: datetime }

  console.debug(`${fn} updatedEntityDataWithModifiedAt...`)
  console.dir(cloneDeep(updatedEntityDataWithModifiedAt))
  return updatedEntityDataWithModifiedAt
}

/**
 * The multi-action save: one request carrying several typed writes.
 *
 * ⚠ DETECTION IS ON THE REQUEST, not the response. The call itself is different
 * — a different endpoint via a different dispatcher — so it has to be known
 * before anything is sent. Detecting early also means the response can be
 * ASSERTED rather than sniffed: a mismatched one throws here instead of falling
 * through into the single-entity branch and writing `undefined` versions onto
 * rows nobody would think to check.
 */
async function _postEntityActions({
  fn: callingFn,
  payload,
  isSubentityDialog,
  afterRequestCallback,
}: {
  fn: string
  payload: IEntityActionsPayload
  isSubentityDialog: boolean
  afterRequestCallback: Function | undefined
}) {
  const fn = `${callingFn} ---> _postEntityActions |`
  assert(
    !isSubentityDialog,
    `${fn} a subentity dialog cannot produce an actions payload.`
  )
  if (!afterRequestCallback) {
    throw new Error(`${fn} There is NO afterRequestCallback`)
  }

  // Nothing to send. The dialog still gets its callback, because today an
  // unchanged options dialog POSTs `[]` and the created-callback runs anyway —
  // dropping it here would be a behaviour change hiding inside a round-trip
  // saving.
  if (payload.actions.length === 0) {
    console.debug(`${fn} no actions. Nothing to write.`)
    runAfterRequestCallback(afterRequestCallback, { newEntityData: [] })
    return
  }

  console.debug(
    `${fn} writing ${payload.actions.length} action(s) for` +
      ` ${[...new Set(payload.actions.map((a) => a.entity))].join(', ')}`
  )
  // ⚠ THE ROWS THEMSELVES, not just a count. `postEntityData` and
  // `patchEntityData` both log the data they are about to send ("Calling
  // createEntity with entityName …, newEntityData:"), and this path returns
  // before either of them — so an /actions write was the one write in the app
  // whose payload never reached the console, which is exactly the write hardest
  // to reason about from the outside.
  //
  // The WIRE body, not `payload`: `toEntityActionsWireBody` strips `__kind`
  // and each action's `afterRequestCallback`, so this is what the server
  // actually receives rather than a superset of it. Cloned for the same
  // reason the create path clones — the rows are mutated in place by the
  // write-back a moment later, and a console reference would otherwise show
  // the post-write state.
  console.debug(
    `${fn} Data being sent to request:`,
    cloneDeep(toEntityActionsWireBody(payload))
  )
  const data = await writeEntityActions(payload)
  assertIsNotUndefined(data)
  assertIsEntityActionsResponse(fn, data, payload)

  // ⚠ TWO PASSES, AND THE ORDER MATTERS. Every write-back happens before any
  // callback: a callback re-renders from the rows it was handed, so a throw
  // partway through the callbacks must not find half the rows carrying new
  // versions and half still holding stale ones. Collapsing this into one loop
  // reintroduces exactly that.
  payload.actions.forEach((sent, index) => {
    const returned = (data as any).actions[index]
    const expectedIds = sent.rows.map((row: any) => row.id)
    if (sent.action === 'update') {
      writeBackVersions({
        fn,
        updatedEntityData: sent.rows,
        data: returned.rows,
        expectedIds,
      })
    } else {
      writeBackHridsAndVersions({
        fn,
        newEntityData: sent.rows,
        data: returned.rows,
        expectedIds,
      })
    }
  })

  // Each action notifies with the argument shape its half of the old
  // two-request flow used, so the callbacks themselves need no changes.
  // `entityId: undefined` matches what an array PATCH has always passed.
  payload.actions.forEach((sent) => {
    if (sent.rows.length === 0) {
      return
    }
    const callback = sent.afterRequestCallback ?? afterRequestCallback
    if (sent.action === 'update') {
      runAfterRequestCallback(callback, {
        updatedEntityData: sent.rows,
        entityId: undefined,
      })
    } else {
      runAfterRequestCallback(callback, { newEntityData: sent.rows })
    }
  })
}
